/**
 * A markdown step's shelf rounding on screen (`./price-rounding.ts`, bata-platform ADR 6190,
 * `docs/adr/9067`): on the step's head the target %, the realised % and the shelf price; in
 * «Perché» each recorded field with what it means; on the approvals row one sentence. A step the
 * run recorded no rounding for draws nothing.
 */

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { PriceRounding } from '@/api/generated';
import { formatCurrency, formatPercent } from '@/lib/format';
import { Badge } from '@/primitives';

import { roundingOf, type RoundingPresentation } from './price-rounding';

/** Shares are shown to one decimal: 23.1% realised against a 20% target is the point. */
const DEPTH_DIGITS = 1;

function depth(value: number | null, language: string, t: TFunction): string {
  return value === null ? t('absent.notStated') : formatPercent(value, language, DEPTH_DIGITS);
}

function modeText(rounding: RoundingPresentation, t: TFunction): string {
  return rounding.modeLabelKey === null ? rounding.mode : t(rounding.modeLabelKey);
}

/** The approvals row's sentence: «Obiettivo 20% · realizzato 23,1% · a scaffale 99.900 COP». */
export function roundingSentence(
  value: PriceRounding | null | undefined, currency: string, language: string, t: TFunction,
): string | null {
  const rounding = roundingOf(value);
  if (rounding === null) return null;
  const said = t('inseason.priceRounding.sentence', {
    target: depth(rounding.targetDepth, language, t),
    realised: depth(rounding.realisedDepth, language, t),
    shelf: rounding.shelfPrice === null ? t('absent.notStated') : formatCurrency(rounding.shelfPrice, currency, language),
  });
  return rounding.merged.length === 0 ? said : t('inseason.priceRounding.sentenceMerged', {
    sentence: said,
    rungs: rounding.merged.map((rung) => formatPercent(rung, language, 0)).join(' + '),
  });
}

/** The step's head: target, realised, shelf price, and a chip when rungs merged. */
export function PriceRoundingFacts({ value, currency }: {
  readonly value: PriceRounding | null | undefined;
  readonly currency: string;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const rounding = roundingOf(value);
  if (rounding === null) return null;
  return (
    <div className="flex flex-col gap-2" data-price-rounding={rounding.mode}>
      <dl className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.target')}</dt>
          <dd className="font-numeric text-numeric tabular-nums" data-rounding-target="">{depth(rounding.targetDepth, i18n.language, t)}</dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.realised')}</dt>
          <dd className="font-numeric text-numeric tabular-nums" data-rounding-realised="">{depth(rounding.realisedDepth, i18n.language, t)}</dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.shelfPrice')}</dt>
          <dd className="font-numeric text-numeric tabular-nums" data-rounding-shelf="">
            {rounding.shelfPrice === null ? t('absent.notStated') : formatCurrency(rounding.shelfPrice, currency, i18n.language)}
          </dd>
        </div>
      </dl>
      {rounding.merged.length > 0 && (
        <span data-rounding-merged={rounding.merged.join(',')}>
          <Badge
            labelKey="inseason.priceRounding.merged"
            values={{ rungs: rounding.merged.map((rung) => formatPercent(rung, i18n.language, 0)).join(' + ') }}
          />
        </span>
      )}
    </div>
  );
}

/** «Perché»: the mode, the multiplier, the two depths, the shelf price, how it got there. */
export function PriceRoundingRows({ value, currency, vatMultiplier }: {
  readonly value: PriceRounding | null | undefined;
  readonly currency: string;
  readonly vatMultiplier: number | null | undefined;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const rounding = roundingOf(value);
  if (rounding === null) return null;
  const extra = rounding.extraDepth;
  return (
    <dl className="grid gap-3 sm:grid-cols-2" data-price-rounding-rows="">
      <div className="flex flex-col gap-1">
        <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.mode')}</dt>
        <dd className="flex flex-col gap-1">
          <span data-rounding-mode={rounding.mode}>{modeText(rounding, t)}</span>
          <span className="text-caption text-ink-secondary max-w-prose">
            {t('inseason.help.priceRounding.basis', {
              vat: typeof vatMultiplier === 'number' && Number.isFinite(vatMultiplier)
                ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 4 }).format(vatMultiplier)
                : t('absent.notStated'),
            })}
          </span>
        </dd>
      </div>
      <div className="flex flex-col gap-1">
        <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.depths')}</dt>
        <dd className="flex flex-col gap-1">
          <span className="font-numeric tabular-nums">
            {t('inseason.priceRounding.depthsValue', {
              target: depth(rounding.targetDepth, i18n.language, t),
              realised: depth(rounding.realisedDepth, i18n.language, t),
            })}
          </span>
          {extra !== null && Math.abs(extra) > 0 && (
            <span className="text-caption text-ink-primary" data-rounding-extra={extra}>
              {t(extra > 0 ? 'inseason.priceRounding.deeper' : 'inseason.priceRounding.shallower', {
                points: formatPercent(Math.abs(extra), i18n.language, DEPTH_DIGITS),
              })}
            </span>
          )}
        </dd>
      </div>
      <div className="flex flex-col gap-1">
        <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.shelfPrice')}</dt>
        <dd className="font-numeric tabular-nums">
          {rounding.shelfPrice === null ? t('absent.notStated') : formatCurrency(rounding.shelfPrice, currency, i18n.language)}
        </dd>
      </div>
      {rounding.direction !== null && (
        <div className="flex flex-col gap-1">
          <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.direction')}</dt>
          <dd className="text-caption text-ink-primary max-w-prose" data-rounding-direction={rounding.direction}>
            {rounding.directionLabelKey === null ? rounding.direction : t(rounding.directionLabelKey)}
          </dd>
        </div>
      )}
      {rounding.merged.length > 0 && (
        <div className="flex flex-col gap-1 sm:col-span-2">
          <dt className="text-caption text-ink-secondary">{t('inseason.priceRounding.mergedTitle')}</dt>
          <dd className="text-caption text-ink-primary max-w-prose">
            {t('inseason.help.priceRounding.merged', {
              rungs: rounding.merged.map((rung) => formatPercent(rung, i18n.language, 0)).join(' + '),
            })}
          </dd>
        </div>
      )}
    </dl>
  );
}
